/* FOOTBALL-THEME :: Recent customer orders carousel.
   <fs-orders> custom element: autoplay scrolls one card at a time and loops
   seamlessly in both directions (a cloned set rides before the cards and
   another after; on settle a pixel-identical instant hop moves it back over
   the seam). Dots jump to a card and follow manual scrolling; mouse drag is
   layered on top of the native touch swipe. Autoplay pauses on hover, focus,
   tab-hide and while dragging; prefers-reduced-motion disables it entirely.
   Vanilla JS, no dependencies. Re-inits on shopify:section:load. */
(function () {
  'use strict';

  if (customElements.get('fs-orders')) return;

  var reduceQuery =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function prefersReduced() {
    return !!(reduceQuery && reduceQuery.matches);
  }

  class FootballOrders extends HTMLElement {
    connectedCallback() {
      this._init();
    }

    disconnectedCallback() {
      this._destroy();
    }

    _init() {
      if (this._inited) this._destroy();

      this.viewport = this.querySelector('.fs-orders__viewport');
      this.track = this.querySelector('.fs-orders__track');
      this.dots = Array.prototype.slice.call(this.querySelectorAll('.fs-orders__dot'));

      var originals = Array.prototype.slice.call(this.querySelectorAll('.fs-orders__card'));
      this.count = originals.length;

      if (!this.viewport || !this.track || this.count < 2) return;

      this._buildClones(originals);
      this.cards = Array.prototype.slice.call(this.querySelectorAll('.fs-orders__card'));

      var speed = parseFloat(this.getAttribute('data-speed'));
      this.speed = (isNaN(speed) ? 5 : Math.min(10, Math.max(3, speed))) * 1000;
      this.autoplay = this.hasAttribute('data-autoplay') && !prefersReduced();

      /* Start on the originals (the left clone set gives backward room).
         Begin at a random review so a repeat visit doesn't look identical. */
      this.index = this.count + Math.floor(Math.random() * this.count);
      this.timer = null;
      this.syncTimer = null;
      this.pending = false;
      this.settleTimer = null;
      this.paused = false;
      this.dragging = false;
      this.startX = 0;
      this.startScroll = 0;

      this._onScroll = this._onScroll.bind(this);
      this._onScrollEnd = this._onScrollEnd.bind(this);
      this._onDotClick = this._onDotClick.bind(this);
      this._onDown = this._onDown.bind(this);
      this._onMove = this._onMove.bind(this);
      this._onUp = this._onUp.bind(this);
      this._onEnter = this._onEnter.bind(this);
      this._onLeave = this._onLeave.bind(this);
      this._onVisibility = this._onVisibility.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onUserInput = this._onUserInput.bind(this);

      this.dots.forEach(function (dot) {
        dot.addEventListener('click', this._onDotClick);
      }, this);

      this.viewport.addEventListener('scroll', this._onScroll, { passive: true });
      this.viewport.addEventListener('scrollend', this._onScrollEnd);
      this.viewport.addEventListener('pointerdown', this._onDown);
      this.viewport.addEventListener('wheel', this._onUserInput, { passive: true });
      this.viewport.addEventListener('touchstart', this._onUserInput, { passive: true });
      window.addEventListener('pointermove', this._onMove);
      window.addEventListener('pointerup', this._onUp);
      window.addEventListener('pointercancel', this._onUp);
      window.addEventListener('resize', this._onResize);
      this.addEventListener('mouseenter', this._onEnter);
      this.addEventListener('mouseleave', this._onLeave);
      this.addEventListener('focusin', this._onEnter);
      this.addEventListener('focusout', this._onLeave);
      document.addEventListener('visibilitychange', this._onVisibility);

      this._jump(this.index);
      this._paint();
      this._start();
      this._inited = true;
    }

    /* Seamless loop in both directions: one cloned set before the originals,
       one after. Scrolling (or autoplay) leans into a clone set; on settle
       _normalize() hops back over the seam while the pixels are identical. */
    _buildClones(originals) {
      var left = document.createDocumentFragment();
      originals.forEach(function (card) {
        left.appendChild(this._makeClone(card));
      }, this);
      this.track.insertBefore(left, originals[0]);
      originals.forEach(function (card) {
        this.track.appendChild(this._makeClone(card));
      }, this);
    }

    _makeClone(card) {
      var clone = card.cloneNode(true);
      clone.classList.add('is-clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-index');
      var focusables = clone.querySelectorAll('a, button, input');
      Array.prototype.forEach.call(focusables, function (el) {
        el.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    _destroy() {
      this._stop();
      var clones = this.querySelectorAll('.fs-orders__card.is-clone');
      Array.prototype.forEach.call(clones, function (clone) {
        if (clone.parentNode) clone.parentNode.removeChild(clone);
      });
      this._settle();
      if (this.syncTimer) {
        window.clearTimeout(this.syncTimer);
        this.syncTimer = null;
      }
      if (this.viewport) {
        this.viewport.removeEventListener('scroll', this._onScroll);
        this.viewport.removeEventListener('scrollend', this._onScrollEnd);
        this.viewport.removeEventListener('pointerdown', this._onDown);
        this.viewport.removeEventListener('wheel', this._onUserInput);
        this.viewport.removeEventListener('touchstart', this._onUserInput);
      }
      if (this.dots) {
        this.dots.forEach(function (dot) {
          dot.removeEventListener('click', this._onDotClick);
        }, this);
      }
      window.removeEventListener('pointermove', this._onMove);
      window.removeEventListener('pointerup', this._onUp);
      window.removeEventListener('pointercancel', this._onUp);
      window.removeEventListener('resize', this._onResize);
      this.removeEventListener('mouseenter', this._onEnter);
      this.removeEventListener('mouseleave', this._onLeave);
      this.removeEventListener('focusin', this._onEnter);
      this.removeEventListener('focusout', this._onLeave);
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._inited = false;
    }

    goTo(target, instant) {
      var total = this.cards.length;
      if (!total) return;
      this.index = ((target % total) + total) % total;
      this._scrollTo(this.index, instant);
      this._paint();
    }

    _scrollTo(i, instant) {
      var card = this.cards[i];
      if (!card || !this.viewport) return;
      var left = this._cardLeft(card);
      var behavior = instant || prefersReduced() ? 'auto' : 'smooth';
      if (typeof this.viewport.scrollTo === 'function') {
        this.viewport.scrollTo({ left: left, behavior: behavior });
      } else {
        this.viewport.scrollLeft = left;
      }
      this._waitForScroll();
    }

    _waitForScroll() {
      var self = this;
      if (this.settleTimer) window.clearTimeout(this.settleTimer);
      this.pending = true;
      this.settleTimer = window.setTimeout(function () {
        self._settle();
        self._normalize();
      }, 1000);
    }

    _settle() {
      if (this.settleTimer) {
        window.clearTimeout(this.settleTimer);
        this.settleTimer = null;
      }
      this.pending = false;
    }

    /* When the carousel settles in either clone set, hop back over the seam
       to the matching original — the pixels are identical, so the loop reads
       seamlessly in both scroll directions. */
    _normalize() {
      if (!this.cards || this.count < 1) return;
      if (this.index >= this.count * 2) {
        this.index -= this.count;
        this._jump(this.index);
      } else if (this.index < this.count) {
        this.index += this.count;
        this._jump(this.index);
      }
    }

    /* Silent instant reposition; suppresses the scroll sync for a moment. */
    _jump(i) {
      var card = this.cards[i];
      if (!card || !this.viewport) return;
      var left = this._cardLeft(card);
      this.pending = true;
      if (typeof this.viewport.scrollTo === 'function') {
        this.viewport.scrollTo({ left: left, behavior: 'auto' });
      } else {
        this.viewport.scrollLeft = left;
      }
      this._paint();
      var self = this;
      window.setTimeout(function () {
        self.pending = false;
      }, 80);
    }

    _cardLeft(card) {
      var vpRect = this.viewport.getBoundingClientRect();
      var cardRect = card.getBoundingClientRect();
      return this.viewport.scrollLeft + cardRect.left - vpRect.left;
    }

    _nearest() {
      var left = this.viewport.scrollLeft;
      var best = 0;
      var min = Infinity;
      for (var i = 0; i < this.cards.length; i++) {
        var d = Math.abs(this._cardLeft(this.cards[i]) - left);
        if (d < min) {
          min = d;
          best = i;
        }
      }
      return best;
    }

    _paint() {
      this.cards.forEach(function (card, i) {
        card.classList.toggle('is-active', i === this.index);
      }, this);

      var logical = this.index % this.count;
      this.dots.forEach(function (dot, i) {
        var on = i === logical;
        dot.classList.toggle('is-active', on);
        if (on) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      }, this);
    }

    _start() {
      this._stop();
      if (!this.autoplay || this.paused || document.hidden) return;
      var self = this;
      this.timer = window.setInterval(function () {
        if (self.paused || self.dragging || document.hidden) return;
        self.goTo(self.index + 1);
      }, this.speed);
    }

    _stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    _onDotClick(event) {
      var i = parseInt(event.currentTarget.getAttribute('data-index'), 10);
      if (!isNaN(i)) this.goTo(this.count + i);
    }

    _onScroll() {
      if (this.pending) return;
      var self = this;
      if (this.syncTimer) window.clearTimeout(this.syncTimer);
      this.syncTimer = window.setTimeout(function () {
        self.syncTimer = null;
        if (self.pending) return;
        var best = self._nearest();
        if (best !== self.index) {
          self.index = best;
          self._paint();
        }
      }, 80);
    }

    _onScrollEnd() {
      this._settle();
      this._normalize();
    }

    _onUserInput() {
      this._settle();
    }

    _onEnter() {
      this.paused = true;
      this._stop();
    }

    _onLeave() {
      this.paused = false;
      this._start();
    }

    _onVisibility() {
      if (document.hidden) this._stop();
      else this._start();
    }

    _onResize() {
      this._scrollTo(this.index, true);
    }

    _onDown(event) {
      if (!event.pointerType || event.pointerType !== 'mouse') return;
      if (event.button !== 0) return;
      if (event.target.closest && event.target.closest('a, button')) return;

      this._settle();
      this.dragging = true;
      this.startX = event.clientX;
      this.startScroll = this.viewport.scrollLeft;
      this.classList.add('is-dragging');
      event.preventDefault();
    }

    _onMove(event) {
      if (!this.dragging) return;
      var dx = event.clientX - this.startX;
      this.viewport.scrollLeft = this.startScroll - dx;
    }

    _onUp() {
      if (!this.dragging) return;
      this.dragging = false;
      this.classList.remove('is-dragging');
      this.goTo(this._nearest());
    }
  }

  customElements.define('fs-orders', FootballOrders);

  document.addEventListener('shopify:section:load', function (event) {
    var el =
      event.target && event.target.querySelector
        ? event.target.querySelector('fs-orders')
        : null;
    if (el && typeof el._init === 'function') el._init();
  });
})();
